import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "galaxy-spiral";

const DEFAULT_BG = "#020617";
const DEFAULT_ARM_A = "#a78bfa";
const DEFAULT_ARM_B = "#22d3ee";

// A galaxy is faked as a handful of spiral arms of dust. Each particle is
// parameterised by a polar (radius, baseAngle) where baseAngle is sampled
// near one of the arms (with a bit of jitter so it looks fluffy). Drawing
// in polar space and converting to cartesian on the fly means we don't
// need any allocations per frame other than the projected x/y.
//
// The whole galaxy rotates slowly. Inner particles rotate faster than outer
// ones (differential rotation), which is what gives real spiral galaxies
// their shape over time and what makes the animation feel alive even at
// extremely slow rotation rates.
const PARTICLE_COUNT = 3200;
const ARM_COUNT = 5;             // 5 arms — more interesting silhouette than the classic 2
const ARM_TIGHTNESS = 1.6;       // radians of twist per unit radius
const RADIUS_SPREAD = 0.55;      // how spread out particles are along radius
const ANGLE_JITTER = 0.26;       // tangential thickness of the arms (kept tight so 5 arms read distinctly)
const ROTATION_SPEED = 0.00018;  // base angular velocity (rad/frame)
const CORE_GLOW_FRAC = 0.18;     // bright bulge in the centre

function rand(min, max) {
  return min + Math.random() * (max - min);
}

// Box-Muller-ish: bias toward 0..1 with a soft falloff (sqrt is easy enough
// for "denser core"). Returns roughly 0..1, weighted toward 0.
function biasedRadius() {
  const u = Math.random();
  return Math.sqrt(u);
}

export default function GalaxySpiral({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_ARM_A, DEFAULT_ARM_B]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_ARM_A),
      pickColor(colors, 2, DEFAULT_ARM_B),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const seedParticles = () => {
      const arr = new Array(PARTICLE_COUNT);
      for (let i = 0; i < PARTICLE_COUNT; i += 1) {
        const arm = i % ARM_COUNT;
        const r = biasedRadius() * RADIUS_SPREAD + 0.02;
        const baseArm = (arm / ARM_COUNT) * Math.PI * 2;
        const jitter = (Math.random() - 0.5) * ANGLE_JITTER * (1 - r * 0.5);
        const baseAngle = baseArm + r * ARM_TIGHTNESS * Math.PI * 2 + jitter;
        // Color by arm; brightness biased toward inner radii so the core
        // glows. Add some halo particles with low brightness for depth.
        const isHalo = Math.random() < 0.18;
        const brightness = isHalo
          ? 0.15 + Math.random() * 0.2
          : 0.45 + (1 - r) * 0.55;
        arr[i] = {
          r: isHalo ? r + 0.1 + Math.random() * 0.18 : r,
          baseAngle,
          // Alternate the two arm colors around the wheel so neighboring
          // arms always contrast. With an odd ARM_COUNT one pair will
          // share a color, which actually helps the galaxy feel less
          // mechanically symmetric.
          slot: (arm % 2) + 1,
          brightness,
          twinkle: Math.random() * Math.PI * 2,
        };
      }
      particlesRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!particlesRef.current.length) seedParticles();
    };

    let t = 0;

    const tick = () => {
      const [bg, cA, cB] = colorsRef.current;
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;
      const baseRadius = Math.min(width, height) * 0.48;

      // Bright core: a soft radial gradient centred on the galaxy.
      const coreR = baseRadius * CORE_GLOW_FRAC * 2;
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
      coreGrad.addColorStop(0, rgba(cA, 0.6));
      coreGrad.addColorStop(0.4, rgba(cA, 0.18));
      coreGrad.addColorStop(1, rgba(cA, 0));
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fill();

      const particles = particlesRef.current;
      ctx.globalCompositeOperation = "lighter";

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        // Differential rotation: inner stuff wraps faster.
        const omega = ROTATION_SPEED * (1 + (1 - p.r) * 1.2);
        const angle = p.baseAngle + t * omega;
        const px = cx + Math.cos(angle) * p.r * baseRadius * 1.6;
        const py = cy + Math.sin(angle) * p.r * baseRadius * 1.0; // squash for slight ellipse

        const tw = 0.7 + 0.3 * Math.sin(t * 0.05 + p.twinkle);
        const a = p.brightness * tw;
        const radius = 0.6 + (1 - p.r) * 1.2;

        ctx.fillStyle = rgba(p.slot === 1 ? cA : cB, a);
        ctx.beginPath();
        ctx.arc(px, py, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = "source-over";

      t += 1;
      rafRef.current = requestAnimationFrame(tick);
    };

    seedParticles();
    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
